import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, PlusCircle, MinusCircle, Ban } from "lucide-react";

/**
 * Combined post-log review: an Ingredient & Macro agent decomposes the meal,
 * shows per-ingredient macros, and lets the user add new items to the pantry or
 * deduct existing ones — all in one dialog.
 * `trigger` = {log_id, name, serving, notes, calories, protein_g, carbs_g, fat_g, fiber_g}
 */
export default function MealReviewDialog({ trigger, onClose, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [updateMacros, setUpdateMacros] = useState(false);

  useEffect(() => {
    if (!trigger) return;
    // Pantry feature is "coming soon" — the post-log pantry review is disabled.
    if (onClose) { onClose(); return; }
    setRows([]); setTotals(null); setUpdateMacros(false);
    (async () => {
      setLoading(true);
      try {
        let data;
        if (trigger.ingredients && trigger.ingredients.length) {
          // Known ingredients (e.g. from a recipe card) — match against pantry, no LLM.
          ({ data } = await api.post("/meals/match", { ingredients: trigger.ingredients }));
        } else {
          ({ data } = await api.post("/meals/analyze", {
            log_id: trigger.log_id || null,
            name: trigger.name,
            serving: trigger.serving || "",
            notes: trigger.notes || "",
            calories: Number(trigger.calories) || 0,
            protein_g: Number(trigger.protein_g) || 0,
            carbs_g: Number(trigger.carbs_g) || 0,
            fat_g: Number(trigger.fat_g) || 0,
            fiber_g: Number(trigger.fiber_g) || 0,
          }));
        }
        setRows(data.ingredients || []);
        setTotals(data.totals || null);
      } catch (e) {
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  const update = (i, key, val) => setRows((arr) => arr.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));

  const apply = async () => {
    const active = rows.filter((r) => r.action !== "skip");
    setApplying(true);
    try {
      const { data } = await api.post("/meals/apply", {
        log_id: trigger.log_id || null,
        ingredients: rows,
        update_macros: updateMacros && totals ? totals : null,
      });
      const parts = [];
      if (data.added?.length) parts.push(`${data.added.length} added to pantry`);
      if (data.deducted?.length) parts.push(`${data.deducted.length} deducted`);
      toast.success(parts.length ? `Pantry updated — ${parts.join(", ")}` : "Reviewed");
      onApplied?.({ ...data, updatedMacros: updateMacros && totals ? totals : null });
    } catch (e) {
      toast.error("Couldn't apply changes");
    } finally {
      setApplying(false);
      onClose?.();
    }
  };

  const open = !!trigger;
  const ActionBtn = ({ active, onClick, icon: Icon, label, color, testid }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition border ${
        active ? color : "border-border text-muted-foreground hover:bg-muted"
      }`}
    >
      <Icon size={12} /> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="rounded-3xl max-w-2xl" data-testid="meal-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <Sparkles size={20} className="text-secondary" /> Review ingredients
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {trigger?.name} — add new items to your pantry or deduct what you used.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-muted-foreground text-sm" data-testid="meal-review-loading">Breaking down ingredients…</div>}
        {!loading && rows.length === 0 && (
          <div className="py-8 text-center text-muted-foreground text-sm">No ingredients identified for this meal.</div>
        )}

        {!loading && rows.length > 0 && (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto scrollbar-thin pr-1" data-testid="meal-review-list">
            {rows.map((r, i) => (
              <div key={i} className="bg-surface rounded-2xl p-3 border border-border" data-testid={`ingredient-row-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-primary truncate">{r.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {r.calories} kcal • P{r.protein_g} C{r.carbs_g} F{r.fat_g} • Fib {r.fiber_g}g
                      {r.in_pantry && <span className="ml-1 text-secondary">• in pantry</span>}
                    </div>
                  </div>
                  <Input
                    type="number"
                    value={r.quantity}
                    onChange={(e) => update(i, "quantity", Number(e.target.value))}
                    className="w-20 h-8 text-right"
                    data-testid={`ingredient-qty-${i}`}
                  />
                  <span className="text-xs text-muted-foreground w-10">{r.unit}</span>
                </div>
                <div className="flex gap-1.5 mt-2">
                  {r.in_pantry ? (
                    <ActionBtn active={r.action === "deduct"} onClick={() => update(i, "action", "deduct")}
                      icon={MinusCircle} label="Deduct" color="bg-primary/10 border-primary/30 text-primary" testid={`ingredient-deduct-${i}`} />
                  ) : (
                    <ActionBtn active={r.action === "add"} onClick={() => update(i, "action", "add")}
                      icon={PlusCircle} label="Add to pantry" color="bg-secondary/15 border-secondary/30 text-secondary" testid={`ingredient-add-${i}`} />
                  )}
                  <ActionBtn active={r.action === "skip"} onClick={() => update(i, "action", "skip")}
                    icon={Ban} label="Skip" color="bg-muted border-border text-foreground/70" testid={`ingredient-skip-${i}`} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && totals && (
          <label className="flex items-center gap-2 mt-1 text-sm cursor-pointer" data-testid="meal-review-update-macros">
            <input type="checkbox" checked={updateMacros} onChange={(e) => setUpdateMacros(e.target.checked)} />
            <span className="text-foreground/80">
              Update meal macros to breakdown total (<b>{totals.calories} kcal</b> • P{totals.protein_g} C{totals.carbs_g} F{totals.fat_g} • Fib {totals.fiber_g}g)
            </span>
          </label>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onClose?.()} className="rounded-full" data-testid="meal-review-skip-btn">Skip</Button>
          <Button onClick={apply} disabled={applying || loading || rows.length === 0} className="rounded-full bg-primary" data-testid="meal-review-confirm-btn">
            {applying ? "Applying…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
